import { useState } from "react";
import { Alert, Button, Divider, InputNumber, Popconfirm, Space, Table, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import ArtifactUploadButton from "./ArtifactUploadButton";
import ActiveVersionSelector from "./ActiveVersionSelector";
import ArtifactDownloadButton from "./ArtifactDownloadButton";
import { KIND_LAUNCHER, findArtifact, formatBytes, versionsOf } from "./artifact-utils";

/**
 * Launcher paketleri. İstemci GetLauncherVersion ile aktif sürüm numarasını görür, kendisininkinden
 * büyükse GetLauncher/{os}/{version} ile indirip kendini günceller.
 */
export default function LauncherTab({ settings, reload }) {
    const { t } = useTranslation();
    const active = settings.launcher?.versionNumber ?? 0;
    const platforms = settings.platforms ?? [];
    const versions = versionsOf(settings.artifacts, KIND_LAUNCHER);

    const [uploadVersion, setUploadVersion] = useState(Math.max(active, ...versions, 0) + 1);

    const activate = async (versionNumber) => {
        const response = await ApiService.ViewerSettings.SetLauncherVersion(versionNumber);
        if (response?.success) reload();
    };

    const remove = async (versionNumber, platform) => {
        const response = await ApiService.ViewerSettings.DeleteArtifact(KIND_LAUNCHER, versionNumber, platform);
        if (response?.success) reload();
    };

    const columns = [
        {
            title: t('Version'),
            dataIndex: 'version',
            render: (version) => (
                <Space>
                    <strong>{version}</strong>
                    {version === active && <Tag color="green">{t('Active')}</Tag>}
                </Space>
            )
        },
        ...platforms.map(platform => ({
            title: platform.displayName,
            key: platform.key,
            render: (_, row) => {
                const artifact = findArtifact(settings.artifacts, KIND_LAUNCHER, row.version, platform.key);
                if (!artifact) {
                    return <Typography.Text type="secondary">—</Typography.Text>;
                }
                return (
                    <Space direction="vertical" size={0}>
                        <span>{formatBytes(artifact.size)}</span>
                        <ArtifactDownloadButton artifact={artifact} type="link" />
                        <Popconfirm title={t('Delete this package?')} onConfirm={() => remove(row.version, platform.key)}>
                            <Button size="small" type="link" danger disabled={row.version === active}>{t('Delete')}</Button>
                        </Popconfirm>
                    </Space>
                );
            }
        }))
    ];

    const rows = versions.map(version => ({ key: version, version }));

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
                type="info"
                showIcon
                message={t('A version can only be activated once its Windows x64 package exists. Missing ARM packages fall back to the x64 build.')}
            />

            <ActiveVersionSelector versions={versions} active={active} onActivate={activate} />

            <Divider style={{ margin: '8px 0' }} />

            <Space align="end" wrap>
                <div>
                    <Typography.Text type="secondary">{t('New version number')}</Typography.Text>
                    <InputNumber min={1} value={uploadVersion} onChange={setUploadVersion} style={{ display: 'block', width: 160 }} />
                </div>
                {platforms.map(platform => (
                    <ArtifactUploadButton
                        key={platform.key}
                        size="small"
                        label={platform.displayName}
                        upload={(file, onProgress) =>
                            ApiService.ViewerSettings.UploadLauncher(uploadVersion, platform.key, file, onProgress)}
                        onUploaded={reload}
                    />
                ))}
            </Space>

            <Table rowKey="key" size="small" pagination={false} dataSource={rows} columns={columns} />
        </Space>
    );
}
